import React from 'react';
import {
  Typography,
  Container,
  Box,
  Card,
  CardContent,
  CardMedia,
  Avatar,
  Chip,
} from '@mui/material';
import { HEADER_HEIGHT } from '../App';

import Grid from '@mui/material/GridLegacy';
import PageHeader from '../components/PageHeader';
import { usePageMeta } from '../hooks/usePageMeta';

const About: React.FC = () => {
  usePageMeta(
    'About Nancy Michail | Good Shepherd Accounting, Padstow',
    'Meet Nancy Michail, principal accountant at Good Shepherd Accounting, with 15 years of hands-on accounting for Sydney businesses.'
  );
  const founder = {
    name: 'Nancy Michail',
    role: 'Principal Accountant & Founder',
    image: require('../assets/headshot.jpg'),
    description: 'I\'ve spent over 15 years helping Sydney businesses and individuals with their accounting. I work hands-on with every client, so you always know who you\'re dealing with and where things stand.',
  };

  return (
    <>
      <PageHeader
        title="About Good Shepherd"
        subtitle="A Sydney accounting practice built on 15 years of personal service."
      />
      <Box id="about-content">
        <Box
          sx={{
            minHeight: '50vh',
            display: 'flex',
            alignItems: 'center',
            py: 8,
            backgroundColor: 'rgba(27,60,105,0.02)',
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ maxWidth: 800, mx: 'auto', textAlign: 'center' }}>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.2rem', mb: 3 }}>
                GS Accountants was founded with a simple mission: to provide
                exceptional accounting services with a truly personal touch. Unlike larger firms
                where clients become just another number, I believe in building genuine
                relationships with each business I serve.
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.2rem' }}>
                Working this way means I get to know your business properly, so my advice fits
                how you actually operate. I work independently, and for complex matters I bring
                in trusted specialists.
              </Typography>
            </Box>
          </Container>
        </Box>

        <Box
          sx={{
            minHeight: '60vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            py: 8,
            background: 'white',
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography 
                variant="h3" 
                component="h2" 
                gutterBottom 
                color="primary.main"
                sx={{
                  fontWeight: 600,
                  position: 'relative',
                  display: 'inline-block',
                  mb: 4,
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 100,
                    height: 4,
                    backgroundColor: 'secondary.main',
                  }
                }}
              >
                Meet Your Accountant
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  maxWidth: 800, 
                  mx: 'auto', 
                  mb: 6,
                  fontSize: '1.1rem',
                  color: 'text.secondary'
                }}
              >
                When you work with GS Accountants you deal with the same person every time,
                someone who knows your history and gets back to you quickly.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Card 
                sx={{ 
                  maxWidth: 600,
                  borderRadius: 4,
                  overflow: 'visible',
                  position: 'relative',
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                  p: 4,
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: 4 }}>
                  <Avatar
                    src={founder.image}
                    alt={founder.name}
                    sx={{
                      width: 200,
                      height: 200,
                      border: '4px solid',
                      borderColor: 'secondary.main',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    }}
                  />
                  <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                    <Typography variant="h4" component="h3" gutterBottom color="primary.main" sx={{ fontWeight: 600 }}>
                      {founder.name}
                    </Typography>
                    <Typography variant="h6" color="secondary.main" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                      {founder.role}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {founder.description}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Box>
          </Container>
        </Box>

        <Box
          sx={{
            minHeight: '50vh',
            display: 'flex',
            alignItems: 'center',
            py: 8,
            backgroundColor: 'rgba(27,60,105,0.02)',
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
              <Typography 
                variant="h3" 
                component="h2" 
                gutterBottom 
                color="primary.main"
                sx={{
                  fontWeight: 600,
                  position: 'relative',
                  display: 'inline-block',
                  mb: 4,
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 100,
                    height: 4,
                    backgroundColor: 'secondary.main',
                  }
                }}
              >
                My Commitment
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
                Good accounting is about more than the numbers. It's about knowing your
                accountant will give you a straight answer and be there when something comes up.
                Every client gets the same attention, whether that's a single tax return or a
                full set of business accounts.
              </Typography>
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default About; 